import { useEffect } from 'react'

const DesiredOutcomeQuestion = ({ formData, setFormData, questionState, setQuestionState, validationErrors = [] }) => {

    useEffect(() => {
        const existingResponses = formData.form_responses?.question5?.responses || [];
        if (existingResponses.length > 0) {
            const newState = { ...questionState };

            existingResponses.forEach(response => {
                if (newState[response.answer_text]) {
                    newState[response.answer_text].selected = true;
                }
            });

            setQuestionState(newState);
        }
    }, [formData.form_responses?.question5?.responses])

    const handleQuestionUpdate = (responseKey, checkedValue, parentKey = null, dataKey = null, value = null) => {
        setQuestionState(prevState => {
            const newState = { ...prevState };

            if (dataKey) {
                newState[responseKey] = {
                    ...newState[responseKey],
                    [dataKey]: value
                };
            } else {
                newState[responseKey] = {
                    ...newState[responseKey],
                    selected: checkedValue
                };
            }

            return newState;
        });

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses?.question5?.responses || [];

            if (dataKey) {
                const updatedResponses = currentResponses.map(response =>
                    response.answer_text === responseKey
                        ? { ...response, [dataKey]: value }
                        : response
                );

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question5: {
                            question_title: "What is the school's intended outcome?",
                            responses: updatedResponses
                        }
                    }
                };
            } else {
                let updatedResponses = [...currentResponses];

                if (checkedValue) {
                    if (!updatedResponses.find(r => r.answer_text === responseKey)) {
                        updatedResponses.push({
                            answer_text: responseKey,
                            sub_responses: []
                        });
                    }
                } else {
                    updatedResponses = updatedResponses.filter(r => r.answer_text !== responseKey);
                }

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question5: {
                            question_title: "What is the school's intended outcome?",
                            responses: updatedResponses
                        }
                    }
                };
            }
        });
    };

    const hasResponse = (responseKey) => {
        return questionState[responseKey]?.selected || false;
    };

    const getResponseData = (responseKey, dataKey) => {
        return questionState[responseKey]?.[dataKey] || '';
    };
    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                    Step 6: What is the school's intended outcome?
                </h2>
                <span className="text-red-500 text-sm font-medium">Required *</span>
            </div>
            <div>
                {validationErrors.includes('no_selection') && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-3 mx-4 mt-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    Please select at least one option to continue.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`${validationErrors.includes('no_selection') ? 'border-2 border-red-200 bg-red-50 m-2 rounded-md' : ''}`}>
                    <div className="p-2">
                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="fitness_advice"
                                    value="Advice On Fitness For Work"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Advice On Fitness For Work', e.target.checked)}
                                    checked={hasResponse('Advice On Fitness For Work')}
                                />
                                <label htmlFor="fitness_advice" className="text-sm text-gray-700">Advice On Fitness For Work</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="adjustments_advice"
                                    value="Advice On Adjustments"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Advice On Adjustments', e.target.checked)}
                                    checked={hasResponse('Advice On Adjustments')}
                                />
                                <label htmlFor="adjustments_advice" className="text-sm text-gray-700">Advice On Adjustments</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="attendance_advice"
                                    value="Advice On Attendance Management"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Advice On Attendance Management', e.target.checked)}
                                    checked={hasResponse('Advice On Attendance Management')}
                                />
                                <label htmlFor="attendance_advice" className="text-sm text-gray-700">Advice On Attendance Management</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="performance_advice"
                                    value="Advice On Performance Management"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Advice On Performance Management', e.target.checked)}
                                    checked={hasResponse('Advice On Performance Management')}
                                />
                                <label htmlFor="performance_advice" className="text-sm text-gray-700">Advice On Performance Management</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="wellbeing_support"
                                    value="Wellbeing Support For Employee"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Wellbeing Support For Employee', e.target.checked)}
                                    checked={hasResponse('Wellbeing Support For Employee')}
                                />
                                <label htmlFor="wellbeing_support" className="text-sm text-gray-700">Wellbeing Support For Employee</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="workplace_risks"
                                    value="Review Of Workplace Risks"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Review Of Workplace Risks', e.target.checked)}
                                    checked={hasResponse('Review Of Workplace Risks')}
                                />
                                <label htmlFor="workplace_risks" className="text-sm text-gray-700">Review Of Workplace Risks</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="behavior_support"
                                    value="Support With Behavior"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Support With Behavior', e.target.checked)}
                                    checked={hasResponse('Support With Behavior')}
                                />
                                <label htmlFor="behavior_support" className="text-sm text-gray-700">Support With Behavior</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="info_only"
                                    value="Information Only"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Information Only', e.target.checked)}
                                    checked={hasResponse('Information Only')}
                                />
                                <label htmlFor="info_only" className="text-sm text-gray-700">Information Only</label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="unsure"
                                    value="Unsure"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Unsure', e.target.checked)}
                                    checked={hasResponse('Unsure')}
                                />
                                <label htmlFor="unsure" className="text-sm text-gray-700">Unsure</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DesiredOutcomeQuestion;